// src/components/communications/ThreadItem.jsx
// Individual thread item - renders SMS bubbles, email cards, and call entries
import React, { useState } from 'react';
const API_BASE = process.env.REACT_APP_API_URL || '';
import {
  Mail, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Play, Pause, Voicemail, FileText, ExternalLink, Loader2,
  Clock, CheckCheck, Check, FolderPlus
} from 'lucide-react';

// Format time for display
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format duration
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// ============================================================================
// SMS MESSAGE COMPONENT
// ============================================================================
function SMSMessage({ item }) {
  const isOutbound = item.direction === 'outbound' || item.direction === 'out';
  const status = item.status || item.metadata?.status;
  const [savingMedia, setSavingMedia] = useState({});

  const handleSaveToDocuments = async (commId, mediaIndex) => {
    setSavingMedia(prev => ({ ...prev, [mediaIndex]: 'saving' }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/uploads/save-from-comm/${commId}/${mediaIndex}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ label: 'MMS Attachment' })
      });
      if (res.ok) {
        setSavingMedia(prev => ({ ...prev, [mediaIndex]: 'saved' }));
      } else {
        setSavingMedia(prev => ({ ...prev, [mediaIndex]: 'error' }));
      }
    } catch {
      setSavingMedia(prev => ({ ...prev, [mediaIndex]: 'error' }));
    }
  };

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className={`max-w-[75%] ${isOutbound ? 'order-2' : 'order-1'}`}>
        <div
          className={`rounded-2xl overflow-hidden ${
            isOutbound
              ? 'bg-amber-500 text-white rounded-br-md'
              : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md shadow-sm'
          }`}
        >
          {item.metadata?.mediaUrls?.length > 0 && (
            <div className="flex flex-col gap-1">
              {item.metadata.mediaUrls.map((media, mIdx) => {
                const isTwilio = media.url?.startsWith('https://api.twilio.com/');
                const src = isTwilio ? `${API_BASE}/uploads/twilio-media/${item.id}/${mIdx}` : media.url;
                const saveState = savingMedia[mIdx];
                return (
                  <div key={mIdx} className="relative group">
                    <a href={src} target="_blank" rel="noopener noreferrer">
                      <img
                        src={src}
                        alt="MMS attachment"
                        className="max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                      />
                    </a>
                    {!isOutbound && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSaveToDocuments(item.id, mIdx); }}
                        disabled={saveState === 'saving' || saveState === 'saved'}
                        className={`absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium shadow-md transition-all ${
                          saveState === 'saved'
                            ? 'bg-green-500 text-white'
                            : saveState === 'saving'
                            ? 'bg-gray-200 text-gray-500'
                            : saveState === 'error'
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-white/90 text-gray-700 hover:bg-white opacity-0 group-hover:opacity-100'
                        }`}
                        title="Save to client documents"
                      >
                        {saveState === 'saving' ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : saveState === 'saved' ? (
                          <><Check className="w-3 h-3" /> Saved</>
                        ) : (
                          <><FolderPlus className="w-3 h-3" /> Save</>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {item.body && (
            <p className="px-4 py-2 text-sm whitespace-pre-wrap break-words">{item.body}</p>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-xs ${isOutbound ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatTime(item.timestamp || item.createdAt)}
          </span>
          {isOutbound && (
            <span className="text-xs text-gray-400">
              {status === 'delivered' ? (
                <CheckCheck className="w-3 h-3 text-blue-500" />
              ) : status === 'sent' ? (
                <Check className="w-3 h-3 text-gray-400" />
              ) : status === 'failed' ? (
                <span className="text-red-500 text-xs">Failed</span>
              ) : (
                <Clock className="w-3 h-3 text-gray-300" />
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EMAIL CARD COMPONENT
// ============================================================================
function EmailCard({ item, onViewFull }) {
  const isOutbound = item.direction === 'outbound' || item.direction === 'out';
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mx-2 mb-3">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Email Header */}
        <div className={`px-4 py-3 border-b border-gray-100 ${isOutbound ? 'bg-amber-50' : 'bg-blue-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isOutbound ? 'bg-amber-100' : 'bg-blue-100'
            }`}>
              <Mail className={`w-4 h-4 ${isOutbound ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {isOutbound ? 'You' : (item.client?.firstName || 'Client')}
                </span>
                <span className="text-xs text-gray-400">
                  {formatTime(item.timestamp || item.createdAt)}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {isOutbound ? 'Sent' : 'Received'}
              </p>
            </div>
          </div>
        </div>

        {/* Email Content */}
        <div className="p-4">
          <h4 className="font-medium text-gray-900 mb-2">
            {item.subject || '(No subject)'}
          </h4>
          <p className={`text-sm text-gray-600 whitespace-pre-wrap ${
            expanded ? '' : 'line-clamp-3'
          }`}>
            {item.body || '(No content)'}
          </p>
          {item.body && item.body.length > 150 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-amber-600 hover:text-amber-700 mt-2 font-medium"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>

        {/* Email Footer */}
        {item.metadata?.attachments && item.metadata.attachments.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500">
              {item.metadata.attachments.length} attachment(s)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CALL ENTRY COMPONENT
// ============================================================================
function CallEntry({ item, playingId, audioLoading, onPlayAudio }) {
  const isOutbound = item.direction === 'outbound' || item.direction === 'out';
  const status = item.metadata?.status || 'completed';
  const duration = item.metadata?.duration;
  const hasRecording = item.metadata?.recording_sid;
  const hasVoicemail = item.metadata?.hasVoicemail && item.metadata?.voicemailUrl;
  const isMissed = status === 'no-answer' || status === 'busy' || status === 'canceled' || status === 'failed';

  // Determine icon and colors
  const CallIcon = isMissed ? PhoneMissed : (isOutbound ? PhoneOutgoing : PhoneIncoming);
  const iconBgColor = isMissed ? 'bg-red-100' : (isOutbound ? 'bg-green-100' : 'bg-blue-100');
  const iconColor = isMissed ? 'text-red-500' : (isOutbound ? 'text-green-500' : 'text-blue-500');

  const isPlayingRecording = playingId === item.metadata?.recording_sid;
  const isPlayingVoicemail = playingId === item.id;

  return (
    <div className="mx-2 mb-3">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Call Header */}
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBgColor}`}>
              <CallIcon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">
                {isOutbound ? 'Outgoing Call' : 'Incoming Call'}
                {isMissed && <span className="text-red-500 ml-2">(Missed)</span>}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatTime(item.timestamp || item.createdAt)}</span>
                {duration && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{formatDuration(parseInt(duration))}</span>
                  </>
                )}
                <span className="text-gray-300">|</span>
                <span className="capitalize">{status}</span>
              </div>
            </div>

            {/* Recording Play Button (non-voicemail) */}
            {hasRecording && !hasVoicemail && (
              <button
                onClick={() => onPlayAudio('recording', item.metadata.recording_sid)}
                disabled={audioLoading}
                className={`p-2 rounded-lg transition-colors ${
                  isPlayingRecording
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                }`}
              >
                {audioLoading && playingId === null ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isPlayingRecording ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Voicemail Section */}
        {hasVoicemail && (
          <div className="border-t border-orange-200 bg-orange-50">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Voicemail className="w-5 h-5 text-orange-600" />
                <span className="font-medium text-orange-700">Voicemail</span>
                {item.metadata?.voicemailDuration && (
                  <span className="text-sm text-orange-500">
                    ({formatDuration(parseInt(item.metadata.voicemailDuration))})
                  </span>
                )}
              </div>

              {/* Play Voicemail Button */}
              <button
                onClick={() => onPlayAudio('voicemail', item.id)}
                disabled={audioLoading}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-colors ${
                  isPlayingVoicemail
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {audioLoading && playingId === null ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isPlayingVoicemail ? (
                  <>
                    <Pause className="w-5 h-5" />
                    Pause Voicemail
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Listen to Voicemail
                  </>
                )}
              </button>

              {/* Voicemail Transcription */}
              {item.metadata?.voicemailTranscription && (
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-700">Transcription</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-sm text-gray-700 italic">
                      "{item.metadata.voicemailTranscription}"
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call Recording Transcription (non-voicemail) */}
        {item.metadata?.transcript && !hasVoicemail && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Call Transcript</span>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {item.metadata.transcript}
            </p>
          </div>
        )}

        {/* Call Notes */}
        {item.body && (
          <div className="border-t border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
            <p className="text-sm text-gray-600">{item.body}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN THREAD ITEM COMPONENT
// ============================================================================
export default function ThreadItem({ item, playingId, audioLoading, onPlayAudio }) {
  const type = item.type;

  switch (type) {
    case 'sms':
      return <SMSMessage item={item} />;
    case 'email':
      return <EmailCard item={item} />;
    case 'call':
      return (
        <CallEntry
          item={item}
          playingId={playingId}
          audioLoading={audioLoading}
          onPlayAudio={onPlayAudio}
        />
      );
    default:
      // Fallback for unknown types - render as SMS
      return <SMSMessage item={item} />;
  }
}
